// @ts-nocheck
/**
 * Registry build launcher — shared across surfaces.
 *
 * Wires any element marked `[data-rcp-ai-submit]` so that clicking it
 * dispatches `birch:open-with-query` to the chat companion with intent=
 * registry_builder, a build `mode`, and the brief + any form metadata in
 * scope. The companion opens, the registry-builder skill pack activates, and
 * the AI runs preview_registry_persona → build_registry_draft. On
 * build_started the companion navigates to /pages/registry-edit.
 *
 * Build mode — `guided` vs `full_build` — is read from the button's
 * `data-rcp-ai-mode` attribute and defaults to `guided`: the assistant asks
 * a few quick questions to personalise before building (with a "just build
 * it / surprise me" escape hatch handled server-side). The mode is sent as a
 * structured signal on the dispatch detail — we deliberately do NOT stuff
 * the questions into the chat message, so the user never sees a wall of
 * injected prose. A surface that wants the old surprise-me behaviour sets
 * `data-rcp-ai-mode="full_build"` on its button. Honoured only when the
 * worker's REGISTRY_GUIDED flag is on; otherwise every build is full_build.
 *
 * Surfaces using this:
 *   - /pages/registry-create — full wizard form (button inside the form,
 *     so dueDate / firstBaby / safetyPriority ride along as metadata).
 *   - /pages/registry-public — reg-ai-build CTA section (button inside a
 *     `[data-rcp-ai-scope]` div, just the brief, no form metadata).
 *
 * Replaces the prior in-page build canvas — all progress shows in chat.
 */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }
  ready(init);

  function init() {
    // Wire every submit button on the page. Each is independent — clicking
    // one only reads from its own scope (form / data-rcp-ai-scope), so
    // multiple CTAs can coexist (e.g. if a layout ever shows two builders).
    const buttons = document.querySelectorAll('[data-rcp-ai-submit]');
    buttons.forEach((button) => {
      button.addEventListener('click', function (ev) {
        ev.preventDefault();
        launchBuild(/** @type {HTMLElement} */ (button));
      });

      // Enter-to-submit: pressing Enter in the brief should launch the build,
      // mirroring the click. Shift+Enter still inserts a newline (the brief is
      // a multi-line textarea). Bind to the brief in this button's own scope so
      // multiple CTAs stay independent. When a scope holds several CTAs (e.g.
      // the split "Surprise Me" / "Let's Build Together" footer share one
      // brief + form), only the first button in DOM order claims Enter — else
      // each keypress would fire every button's launch and double-dispatch.
      const scope = button.closest('form, [data-rcp-ai-scope]') || document;
      const briefEl =
        scope.querySelector('textarea[name="brief"]') ||
        scope.querySelector('[data-rcp-ai-brief]');
      if (briefEl && !briefEl.dataset.rcpEnterBound) {
        briefEl.dataset.rcpEnterBound = '1';
        briefEl.addEventListener('keydown', function (ev) {
          if (ev.key === 'Enter' && !ev.shiftKey && !ev.isComposing) {
            ev.preventDefault();
            if (!button.disabled) launchBuild(/** @type {HTMLElement} */ (button));
          }
        });
      }
    });
  }

  /**
   * Launch the build for a specific submit button. Scope (the nearest <form>
   * or [data-rcp-ai-scope]) defines where to find the brief textarea and
   * any optional form metadata.
   */
  function launchBuild(button) {
    // As soon as a build CTA is clicked, bring the builder section
    // (#rcp-browse) into view so the canvas/picks are in the viewport while
    // the chat-driven build spins up. Optional chaining keeps this a no-op on
    // surfaces that don't render the builder (e.g. the reg-ai-build CTA).
    document.getElementById('rcp-browse')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const scope = button.closest('form, [data-rcp-ai-scope]') || document;
    // Brief lookup, in priority order:
    //   1. textarea[name="brief"] inside the form (registry-create wizard)
    //   2. [data-rcp-ai-brief] anywhere in the scope (reg-ai-build CTA and
    //      future surfaces that don't want a full form).
    const briefEl =
      scope.querySelector('textarea[name="brief"]') ||
      scope.querySelector('[data-rcp-ai-brief]');
    const brief = briefEl ? String(briefEl.value || '').trim() : '';

    // Metadata is only meaningful when the scope is a real <form> — those
    // surfaces gather extra fields (dueDate, firstBaby, safetyPriority).
    // For lighter surfaces the metadata is just { brief }.
    const isForm = scope.tagName === 'FORM';
    const metadata = isForm ? collectFormMetadata(/** @type {HTMLFormElement} */ (scope)) : {};

    // Build mode signal — guided (ask a few questions first) by default; a
    // button can opt into the old surprise-me path with data-rcp-ai-mode.
    // Sent structured so the backend's GUIDED_INTAKE_FRAGMENT drives the
    // questions — no injected prose in the user-visible message.
    const mode = button.getAttribute('data-rcp-ai-mode') === 'full_build'
      ? 'full_build'
      : 'guided';

    // Default message when the brief is empty depends on the mode:
    //   • full_build ("Surprise Me") → build the complete registry straight
    //     away from the profile, then offer to personalise/modify after.
    //   • guided ("Let's Build Together") → "Help me build a baby registry",
    //     which signals the assistant to ask a few questions first and only
    //     then build.
    // A typed brief always wins over the default for either mode.
    const defaultMessage = mode === 'full_build'
      ? 'Build me a complete registry.'
      : 'Help me build a baby registry';
    const chatMessage = brief.length > 0 ? brief : defaultMessage;

    // Registry target signal (multi-registry rules):
    //   • A registry is in view → resume it: pass its id. With eager creation the
    //     registry-edit page ALWAYS has a concrete registry (the builder mints an
    //     empty draft on entry), so __birchActiveRegistryId is set even for a
    //     brand-new empty registry — the build resumes THAT one instead of
    //     creating a duplicate.
    //   • Off the builder (e.g. the registry-public reg-ai-build CTA) there's no
    //     registry in view → start fresh: the build creates a new registry. The
    //     build buttons never show a picker.
    var activeRegistryId = (typeof window.__birchActiveRegistryId === 'string' && window.__birchActiveRegistryId)
      ? window.__birchActiveRegistryId
      : null;

    // Signed-in customer email (builder form) — rides through to the bulk insert
    // so the server fires REGISTRY_CREATED on the registry's 0→1 item transition.
    var customerEmail = '';
    try {
      var cform = document.getElementById('registry-create-form');
      customerEmail = (cform && cform.dataset && cform.dataset.customerEmail) || window.__birchCustomerEmail || '';
    } catch (_e) { /* no form in scope */ }

    // Fire — chat panel opens, intent + mode + metadata ride along.
    window.dispatchEvent(new CustomEvent('birch:open-with-query', {
      detail: Object.assign({
        query: chatMessage,
        fresh: true,
        intent: 'registry_builder',
        mode: mode,
        metadata: Object.assign({ brief, customerEmail: customerEmail }, metadata),
      }, activeRegistryId ? { registryId: activeRegistryId } : { startNew: true }),
    }));

    // Indicate to the user that the chat is now driving — keep the source
    // surface visible but disable resubmission until something happens. If
    // the chat asks a question, the button stays greyed; if a build
    // starts, the companion navigates away. Re-enable after a max wait so
    // the user can retry if nothing happens.
    button.disabled = true;
    if (!button.dataset.originalLabel) button.dataset.originalLabel = button.innerHTML;
    button.innerHTML = '<span class="rcp-launching-spinner" aria-hidden="true"></span> BirchAI is building…';
    setTimeout(function () {
      button.disabled = false;
      button.innerHTML = button.dataset.originalLabel || 'Build My Registry';
    }, 60000);
  }

  /**
   * Collect optional metadata from a wizard form. Fields not present are
   * omitted so the chat payload stays small.
   */
  function collectFormMetadata(form) {
    const out = {};
    const fd = new FormData(form);
    const dd = fd.get('dueDate'); if (dd) out.dueDate = String(dd);
    const fb = fd.get('firstBaby'); if (fb) out.firstBaby = String(fb);
    const sp = fd.get('safetyPriority'); if (sp) out.safetyPriority = String(sp);
    // Preset the user previewed (hidden input set by registry-unified.js).
    // Preview pills don't fill the brief — this lets the worker seed it.
    const pi = fd.get('presetId'); if (pi) out.presetId = String(pi);
    return out;
  }
})();
